import {
  Avatar,
  Box,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { DropDownItemModel, SideMenuDropdown } from './side-menu-dropdown';
import { FormattedMessage, useIntl } from 'react-intl';
import React, { ChangeEvent, FC, useCallback, useState } from 'react';
import {
  useAuth,
  useCities,
  useDialog,
  useGuest,
  useSideMenu,
  useSnack,
} from '@/logic/state';
import {
  useGetCommunicationLanguages,
  useUpdateCommunicationLanguage,
} from '@/logic/services';
import { useSwitchLang, useUpdateUserSelectedCity } from '@/logic/hooks';

import { ALLOW_SIDE_MENU_ITEMS_PREFETCH } from '@/constants/prefetch';

import CustomizedCircularProgress from '@/components/shared/customized-circular-progress';

import { ILocale } from '@/lang/types';
import Icon24 from '../shared/icon-24';
import Link from 'next/link';

import MyBalanceBadge from '../../features/wallet/components/wallet-access/my-balance-badge';
import { PATHS } from '@/constants/routes';

import { getPages } from '@/constants/side-menu';
import { isWalletFeatureEnabled } from '@/constants/wallet';
import { locales } from '@/lang/locales';
import { pxToRem } from '@/theme/typography';
import shallow from 'zustand/shallow';
import { useNotifications } from '@/hooks/use-notifications';
import { useRouter } from 'next/router';
import { CloseButton } from '../shared/close-button';
import ImageBoxFuture from '../shared/image-box-future';

const ArrowDownIcon = '/icons/shared/arrow-down.svg';
const ArrowUpIcon = '/icons/side-menu/arrow-up.svg';
const AvatarImg = '/icons/side-menu/avatar.svg';
const SideMenuCloseIcon = '/icons/side-menu/close.svg';
const LocationFilledIcon = '/icons/side-menu/location-filled.svg';
const LocationWhiteIcon = '/icons/side-menu/location-white.svg';
const LogoutSideMenuIcon = '/icons/side-menu/logout-side-menu-icon.svg';
const GlobeGreyIcon = '/icons/side-menu/globe-grey.svg';
const GlobeWhiteIcon = '/icons/side-menu/globe-white.svg';

// TODO: change view profile button to link or button in case of logged in user or logged out user
export const SideMenu: FC = () => {
  const theme = useTheme();
  const intl = useIntl();
  const router = useRouter();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('extraLargeDesktop'));
  const [isCitiesDropDownOpen, setIsCitiesDropDownOpen] = useState<boolean>(false);
  const [isCommunicationLanguagesDropDownOpen, setIsCommunicationLanguagesDropDownOpen] =
    useState<boolean>(false);
  const [isCitiesTransitionStarted, setIsCitiesTransitionStarted] =
    useState<boolean>(false);
  const [
    isCommunicationLanguagesTransitionStarted,
    setIsCommunicationLanguagesTransitionStarted,
  ] = useState(false);

  const setSnack = useSnack(s => s.setSnack, shallow);
  const setDialog = useDialog(s => s.setDialog, shallow);
  const [isSideMenuOpen, setSideMenu] = useSideMenu(
    s => [s.isSideMenuOpen, s.setSideMenu],
    shallow
  );
  const cities = useCities(s => s.cities, shallow);
  const [guest, setGuest] = useGuest(s => [s.guest, s.setGuest], shallow);
  const [user, setUser] = useAuth(s => [s.user, s.setUser], shallow);

  const { data: communicationLanguages } = useGetCommunicationLanguages();
  const updateCommunicationLanguage = useUpdateCommunicationLanguage();
  const { isAllNotificationsAreRead } = useNotifications();
  const citiesData: DropDownItemModel[] = cities.citiesList.map(city => ({
    id: city.id,
    value: city.name,
  }));

  const handleCloseSideMenu = useCallback(() => {
    setSideMenu(false);
    setIsCitiesDropDownOpen(false);
    setIsCommunicationLanguagesDropDownOpen(false);
  }, [setSideMenu]);

  const updateUserSelectedCity = useUpdateUserSelectedCity(handleCloseSideMenu);

  const communicationLanguagesData: DropDownItemModel[] =
    communicationLanguages?.communicationLanguage.map(communicationLanguage => ({
      id: communicationLanguage.id,
      value: communicationLanguage.language,
    })) ?? [];

  const getSelectedCityName = useCallback(
    (selectedCityId: number) => {
      return (
        cities.citiesList.find(city => city.id === selectedCityId)?.name ??
        intl.formatMessage({
          id: 'global.city',
        })
      );
    },
    [cities.citiesList, intl]
  );

  const handleLogoutClick = useCallback(() => {
    handleCloseSideMenu();
    setDialog({
      wrapper: true,
      isLogoutConfirmDialog: true,
    });
  }, [handleCloseSideMenu, setDialog]);

  const handleCommunicationLanguageChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      updateCommunicationLanguage.safeMutateAsync(
        {
          data: {
            communicationLanguageId: Number(e.target.value),
          },
        },
        {
          onSuccess: data => {
            const { language: _, ...restUserData } = data.user;
            setUser(prevState => ({
              ...prevState,
              ...restUserData,
              communicationLanguageId: data.user.communicationLanguageId,
            }));
            setSnack({
              isOpen: true,
              message: 'communication_languages.update.success',
              type: 'success',
            });
            handleCloseSideMenu();
          },
        }
      );
    },
    [handleCloseSideMenu, setSnack, setUser, updateCommunicationLanguage]
  );

  const { switchLang } = useSwitchLang();

  const handleLanguageChange = useCallback(
    (language: ILocale) => {
      if (user) {
        setUser(prevState => ({
          ...prevState,
          language,
        }));
      } else {
        setGuest(prevState => ({
          ...prevState,
          language,
        }));
      }
      switchLang(language);
      handleCloseSideMenu();
      setSnack({
        isOpen: true,
        message: 'languages.update.success',
        type: 'success',
      });
    },
    [user, switchLang, handleCloseSideMenu, setSnack, setUser, setGuest]
  );

  const selectedLanguage = router.locale;

  const openNotificationsPopover = (): void => {
    setDialog({
      wrapper: true,
      isNotificationPopover: true,
    });
    setSideMenu(false);
  };

  const checkIfLinkIsActive = (path: string): boolean => {
    const currentRoute = router.pathname.split('/');
    if (!currentRoute || !currentRoute?.length) return;
    if (currentRoute[1] === (path as PATHS)) {
      return true;
    }
    return false;
  };

  return (
    <Drawer
      variant='temporary'
      open={isSideMenuOpen}
      onClose={handleCloseSideMenu}
      ModalProps={{
        keepMounted: true,
      }}
      sx={{
        '& .MuiDrawer-paper': {
          width: {
            mobile: '300px',
            mediumDesktop: '367px',
          },
          paddingX: '10px',
          paddingY: '24px',
          borderRadius: '0 12px 12px 0',

          overflowY: { tablet: 'auto', mobile: 'scroll' },
        },
      }}
    >
      <CloseButton
        icon={SideMenuCloseIcon}
        sx={{
          position: 'static',
          alignSelf: 'flex-end',
        }}
        handleClick={handleCloseSideMenu}
      />
      <Stack
        direction='row'
        alignItems='center'
        spacing={3}
        sx={{
          paddingX: '14px',
        }}
      >
        <Avatar
          sx={{
            width: {
              mobile: '70px',
              tablet: '84px',
            },
            height: {
              mobile: '70px',
              tablet: '84px',
            },
          }}
        >
          <ImageBoxFuture
            img={user?.photoUrl ?? AvatarImg}
            title={intl.formatMessage({
              id: user?.photoUrl ? 'alt.user_img' : 'alt.avatar',
            })}
            objectFit='cover'
            sx={{
              width: '100%',
              height: '100%',
            }}
          />
        </Avatar>
        <Stack spacing={1}>
          <Typography
            component='span'
            sx={{
              color: theme.palette.primary.main,
              fontSize: pxToRem(20),
              fontFamily: 'co-head-line-corp-bold',
              overflowWrap: 'anywhere',
            }}
          >
            <FormattedMessage
              id={
                user && user.firstName
                  ? 'sideMenu.loggedInUserGreet'
                  : 'sideMenu.guestGreet'
              }
              values={{
                name: user?.firstName,
              }}
            />
          </Typography>
          {user && (
            <Link
              href={{
                pathname: PATHS.USER_AREA,
              }}
            >
              <Button
                variant='darkGrey'
                href='/user-area'
                fullWidth={false}
                sx={{
                  fontSize: pxToRem(14),
                  fontFamily: 'co-head-line-corp-bold',
                  lineHeight: 24 / 14,
                  paddingX: '35px',
                  paddingY: '8px',
                  borderRadius: '12px',
                }}
                onClick={() => {
                  handleCloseSideMenu();
                }}
              >
                <FormattedMessage id='sideMenu.viewProfile' />
              </Button>
            </Link>
          )}
          {!user && (
            <Button
              id='sidebar-login-button'
              variant='darkGrey'
              fullWidth={false}
              sx={{
                fontSize: pxToRem(14),
                fontFamily: 'co-head-line-corp-bold',
                lineHeight: 24 / 14,
                paddingX: '35px',
                paddingY: '8px',
                borderRadius: '12px',
              }}
              onClick={() => {
                handleCloseSideMenu();
                setDialog(prevState => ({
                  ...prevState,
                  wrapper: true,
                  isPhoneNumberForm: true,
                }));
              }}
            >
              <FormattedMessage id='sideMenu.login' />
            </Button>
          )}
        </Stack>
      </Stack>
      <Stack
        direction='row'
        alignItems='center'
        sx={{
          marginTop: '52px',
          marginBottom: '10px',
          paddingX: '14px',
        }}
      >
        <Box
          sx={{
            minWidth: 40,
          }}
        >
          <Icon24 icon={GlobeGreyIcon} title='alt.globe_icon' />
        </Box>

        <Stack direction='row' alignItems='center' spacing={3}>
          <Button
            sx={{
              minWidth: 'auto',
              fontSize: pxToRem(18),
              lineHeight: 27 / 18,
              letterSpacing: pxToRem(0.1),
              color: theme.palette.grey[60],
              opacity: selectedLanguage === locales.EN ? 1 : 0.6,
            }}
            onClick={() => handleLanguageChange(locales.EN)}
          >
            English
          </Button>
          <Button
            sx={{
              minWidth: 'auto',
              color: theme.palette.grey[60],
              opacity: selectedLanguage === locales.AR ? 1 : 0.6,
            }}
            onClick={() => handleLanguageChange(locales.AR)}
          >
            عربي
          </Button>
        </Stack>
      </Stack>
      {isWalletFeatureEnabled && <MyBalanceBadge />}
      <ListItem
        disableGutters
        component='div'
        sx={{
          borderRadius: '12px',
          marginBottom: '2.5px',
          backgroundColor: isCitiesTransitionStarted
            ? theme.palette.primary.main
            : 'transparent',
          display: {
            mobile: 'block',
            mediumDesktop: 'none',
          },
        }}
      >
        <ListItemButton
          component='button'
          disableGutters
          sx={{
            borderRadius: '12px',
            paddingX: '14px',
            width: '100%',
            ':hover': {
              backgroundColor: 'transparent',
            },
          }}
          onClick={() => setIsCitiesDropDownOpen(prevState => !prevState)}
        >
          <ListItemIcon
            sx={{
              minWidth: 40,
            }}
          >
            {updateUserSelectedCity.isLoading ? (
              <CustomizedCircularProgress size={15} thickness={4} />
            ) : (
              <Icon24
                icon={isCitiesTransitionStarted ? LocationWhiteIcon : LocationFilledIcon}
                title='images.title.location'
              />
            )}
          </ListItemIcon>
          <ListItemText
            sx={{
              '& span': {
                fontSize: pxToRem(18),
                letterSpacing: pxToRem(0.1),
                color: isCitiesTransitionStarted
                  ? theme.palette.common.white
                  : theme.palette.grey[60],
              },
            }}
          >
            {user?.preferredCityId ? (
              getSelectedCityName(user.preferredCityId)
            ) : guest?.preferredCityId ? (
              getSelectedCityName(guest.preferredCityId)
            ) : (
              <FormattedMessage id='global.city' />
            )}
          </ListItemText>

          <Icon24
            icon={isCitiesTransitionStarted ? ArrowUpIcon : ArrowDownIcon}
            title={
              isCitiesTransitionStarted ? 'alt.arrow_up_icon' : 'alt.arrow_down_icon'
            }
          />
        </ListItemButton>
        <SideMenuDropdown
          isOpen={isCitiesDropDownOpen}
          dropDownTitle='city'
          selectedValue={
            user ? user.preferredCityId : guest ? guest.preferredCityId : null
          }
          data={citiesData}
          onChange={updateUserSelectedCity.update}
          onTransitionEnter={() => setIsCitiesTransitionStarted(true)}
          onTransitionExited={() => setIsCitiesTransitionStarted(false)}
        />
      </ListItem>
      <Box component='nav'>
        <List
          sx={{
            padding: '0px',
          }}
        >
          {getPages(isAllNotificationsAreRead, openNotificationsPopover).map(
            ({ title, img, path, isResponsive, isAuth, isLink, event }) =>
              (isAuth && user) || !isAuth ? (
                <ListItem
                  key={title}
                  disableGutters
                  sx={{
                    paddingY: '10.5px',
                    display: {
                      mobile: 'flex',
                      mediumDesktop: isResponsive ? 'none' : 'flex',
                    },
                  }}
                >
                  {isLink ? (
                    <Link
                      href={{
                        pathname: `/${path}`,
                      }}
                      passHref
                      prefetch={ALLOW_SIDE_MENU_ITEMS_PREFETCH}
                    >
                      <ListItemButton
                        component='a'
                        disableGutters
                        sx={{
                          borderRadius: '12px',
                          paddingX: '14px',
                          backgroundColor:
                            checkIfLinkIsActive(path) && theme.palette.lightGrey.main,
                        }}
                        onClick={() => setSideMenu(false)}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 40,
                          }}
                        >
                          <Icon24 icon={img} title={`nav.link.${title}`} />
                        </ListItemIcon>
                        <ListItemText
                          sx={{
                            '& span': {
                              fontSize: pxToRem(18),
                              letterSpacing: pxToRem(0.1),
                              color: checkIfLinkIsActive(path)
                                ? theme.palette.primary.main
                                : theme.palette.grey[60],
                            },
                          }}
                        >
                          <FormattedMessage id={`nav.link.${title}`} />
                        </ListItemText>
                      </ListItemButton>
                    </Link>
                  ) : (
                    <Button fullWidth>
                      <ListItemButton
                        component='a'
                        disableGutters
                        sx={{
                          '& span': {
                            fontSize: pxToRem(18),
                            letterSpacing: pxToRem(0.1),
                            color: checkIfLinkIsActive(path)
                              ? theme.palette.primary.main
                              : theme.palette.grey[60],
                          },
                          borderRadius: '12px',
                          paddingX: '14px',
                        }}
                        onClick={() => event()}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 40,
                          }}
                        >
                          <Icon24 icon={img} title={`nav.link.${title}`} />
                        </ListItemIcon>
                        <ListItemText
                          sx={{
                            '& span': {
                              fontSize: pxToRem(18),
                              letterSpacing: pxToRem(0.1),
                            },
                          }}
                        >
                          <FormattedMessage id={`nav.link.${title}`} />
                        </ListItemText>
                      </ListItemButton>
                    </Button>
                  )}
                </ListItem>
              ) : null
          )}
        </List>
      </Box>
      {user && (
        <ListItem
          disableGutters
          component='div'
          sx={{
            display: 'block',
            borderRadius: '12px',
            marginTop: '2.5px',
            marginBottom: user ? '15.5px' : '0px',
            backgroundColor: isCommunicationLanguagesTransitionStarted
              ? theme.palette.primary.main
              : 'transparent',
          }}
        >
          <ListItemButton
            component='button'
            disableGutters
            sx={{
              borderRadius: '12px',
              paddingX: '14px',
              width: '100%',
              ':hover': {
                backgroundColor: 'transparent',
              },
            }}
            onClick={() =>
              setIsCommunicationLanguagesDropDownOpen(prevState => !prevState)
            }
          >
            <ListItemIcon
              sx={{
                minWidth: 40,
              }}
            >
              {updateCommunicationLanguage.isLoading ? (
                <CustomizedCircularProgress
                  size={isMediumScreen ? 15 : 24}
                  thickness={4}
                />
              ) : (
                <Icon24
                  icon={
                    isCommunicationLanguagesTransitionStarted
                      ? GlobeWhiteIcon
                      : GlobeGreyIcon
                  }
                  title='alt.globe_icon'
                />
              )}
            </ListItemIcon>
            <ListItemText
              sx={{
                '& span': {
                  fontSize: pxToRem(18),
                  letterSpacing: pxToRem(0.1),
                  color: isCommunicationLanguagesTransitionStarted
                    ? theme.palette.common.white
                    : theme.palette.grey[60],
                },
              }}
            >
              <FormattedMessage id='sideMenu.communicationLanguage' />
            </ListItemText>

            <Icon24
              icon={
                isCommunicationLanguagesTransitionStarted ? ArrowUpIcon : ArrowDownIcon
              }
              title={
                isCommunicationLanguagesTransitionStarted
                  ? 'alt.arrow_up_icon'
                  : 'alt.arrow_down_icon'
              }
            />
          </ListItemButton>
          <SideMenuDropdown
            isOpen={isCommunicationLanguagesDropDownOpen}
            dropDownTitle='communicationLanguage'
            selectedValue={user.communicationLanguageId}
            data={communicationLanguagesData}
            onChange={handleCommunicationLanguageChange}
            onTransitionEnter={() => setIsCommunicationLanguagesTransitionStarted(true)}
            onTransitionExited={() => setIsCommunicationLanguagesTransitionStarted(false)}
          />
        </ListItem>
      )}
      {user && (
        <ListItem
          component='div'
          disableGutters
          sx={{
            marginTop: 'auto',
          }}
        >
          <ListItemButton
            component='a'
            disableGutters
            sx={{
              borderRadius: '12px',
              paddingX: '14px',
            }}
            onClick={handleLogoutClick}
          >
            <ListItemIcon
              sx={{
                minWidth: 40,
              }}
            >
              <ImageBoxFuture
                img={LogoutSideMenuIcon}
                title={intl.formatMessage({
                  id: 'alt.logout_icon',
                })}
                objectFit='contain'
                sx={{
                  width: '24px',
                  height: '24px',
                }}
              />
            </ListItemIcon>
            <ListItemText
              sx={{
                '& span': {
                  fontSize: pxToRem(18),
                  letterSpacing: pxToRem(0.1),
                  color: theme.palette.grey[60],
                },
              }}
            >
              <FormattedMessage id='nav.link.logout' />
            </ListItemText>
          </ListItemButton>
        </ListItem>
      )}
    </Drawer>
  );
};
